"use client";

import { c as _c } from "react/compiler-runtime";
import { IconAlertTriangle, IconRefresh } from "@tabler/icons-react";
import { useQueryErrorResetBoundary } from "@tanstack/react-query";
import { catchError, type ErrorInfo } from "next/error";
import posthogClient from "posthog-js";
import { useEffect } from "react";
import { CreateIssueButton } from "@/components/create-issue-button";
import { Button } from "@domainstack/ui/button";
import { Empty, EmptyContent, EmptyDescription, EmptyHeader, EmptyMedia, EmptyTitle } from "@domainstack/ui/empty";
interface Props {
  /** Display name for the report section (e.g., "Registration", "DNS") */
  sectionName: string;
}
function SectionErrorFallback(t0) {
  const $ = _c(22);
  const {
    sectionName,
    error,
    retry
  } = t0;
  const {
    reset: resetQueryErrors
  } = useQueryErrorResetBoundary();
  const isDev = process.env.NODE_ENV === "development";
  const errorObj = error instanceof Error ? error : undefined;
  let t1;
  let t2;
  if ($[0] !== errorObj || $[1] !== sectionName) {
    t1 = () => {
      if (errorObj) {
        posthogClient.captureException(errorObj, {
          section: sectionName
        });
      }
    };
    t2 = [errorObj, sectionName];
    $[0] = errorObj;
    $[1] = sectionName;
    $[2] = t1;
    $[3] = t2;
  } else {
    t1 = $[2];
    t2 = $[3];
  }
  useEffect(t1, t2);
  let t3;
  let t4;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <EmptyMedia variant="icon"><IconAlertTriangle /></EmptyMedia>;
    t4 = <EmptyTitle>Something went wrong</EmptyTitle>;
    $[4] = t3;
    $[5] = t4;
  } else {
    t3 = $[4];
    t4 = $[5];
  }
  const t5 = isDev && errorObj ? errorObj.message : "This section encountered an error and couldn't be displayed.";
  let t6;
  if ($[6] !== t5) {
    t6 = <EmptyHeader>{t3}{t4}<EmptyDescription>{t5}</EmptyDescription></EmptyHeader>;
    $[6] = t5;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  let t7;
  if ($[8] !== resetQueryErrors || $[9] !== retry) {
    t7 = () => {
      resetQueryErrors();
      retry();
    };
    $[8] = resetQueryErrors;
    $[9] = retry;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  let t8;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <IconRefresh />;
    $[11] = t8;
  } else {
    t8 = $[11];
  }
  let t9;
  if ($[12] !== t7) {
    t9 = <Button variant="outline" size="sm" onClick={t7}>{t8}Retry</Button>;
    $[12] = t7;
    $[13] = t9;
  } else {
    t9 = $[13];
  }
  let t10;
  if ($[14] !== errorObj) {
    t10 = <CreateIssueButton error={errorObj} variant="outline" size="sm" />;
    $[14] = errorObj;
    $[15] = t10;
  } else {
    t10 = $[15];
  }
  let t11;
  if ($[16] !== t10 || $[17] !== t9) {
    t11 = <EmptyContent><div className="flex flex-wrap items-center justify-center gap-2">{t9}{t10}</div></EmptyContent>;
    $[16] = t10;
    $[17] = t9;
    $[18] = t11;
  } else {
    t11 = $[18];
  }
  let t12;
  if ($[19] !== t11 || $[20] !== t6) {
    t12 = <Empty className="border border-solid bg-background/60">{t6}{t11}</Empty>;
    $[19] = t11;
    $[20] = t6;
    $[21] = t12;
  } else {
    t12 = $[21];
  }
  return t12;
}

/**
 * Error boundary for individual domain sections.
 * Catches rendering errors and provides a fallback UI without crashing the entire page.
 * Resets React Query's cached errors on retry.
 */
export const SectionErrorBoundary = catchError((props: Props, errorInfo: ErrorInfo) => <SectionErrorFallback {...props} {...errorInfo} />);